import React, { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux';
import {
  Card,
  Empty,
  Skeleton,
  Space,
  Button
} from "antd";
import { Typography } from "antd";
import { 
  LeftOutlined,
  RightOutlined
} from "@ant-design/icons";
import { useTranslation } from 'react-i18next';
import RequestCard from './RequestCard.jsx';
import { getRequestsByApprovers } from '../../services/requestServices.js';
import { useRequestWorkflows } from '../../hooks/useRequestWorkflows.js';
const { Title, Paragraph, Text } = Typography;

const ApprovalsPending = (props:any) => {
  const { t } = useTranslation();
  const [data, setData] = useState([])
  const [loader, setLoader] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const { userInfo } = useSelector((state: any) => state.user)
  let userEmail = localStorage.getItem("userEmail")
  const spaces = props.spaces;

    // Get email from localStorage first, fallback to redux
    const getEmail = useCallback(() => {
      const localEmail = localStorage.getItem("userEmail");
      return localEmail || userInfo?.email || "";
    }, [userInfo?.email]);

  // Pagination constants
  const ITEMS_PER_PAGE = 4; // Always show 4 cards per page
  // Ensure data is always an array to prevent slice() errors
  const dataArray = Array.isArray(data) ? data : [];
  const totalPages = Math.ceil(dataArray.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentItems = dataArray.slice(startIndex, endIndex);
  // Holds the step translations, so a language switch only re-renders the cards.
  const workflows: Record<string, any> = useRequestWorkflows(dataArray);
  // Responsive breakpoint detection
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  
  const fetchApproverRequests = useCallback(async () => {
      const email = getEmail();
    if (!email) {
      setLoader(false);
      return;
    }

  try {
      setLoader(true)
      let approversResp = await getRequestsByApprovers(email)
      // Ensure we always set an array, even if the response is unexpected
      const responseData = approversResp.data;
      if (Array.isArray(responseData)) {
        setData(responseData);
      } else {
        setData([]);
      }
      setCurrentPage(1) // Reset to first page when data changes
    } catch (error) {
      // Set empty array on error to prevent slice() issues
      setData([])
    } finally {
      setLoader(false)
    }  }, [])

  // Pagination handlers
  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1))
  }

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages))
  }
  
  useEffect(() => {
    fetchApproverRequests();
  }, [])
  return (
    <Card
      className="responsive-approvals-admin rounded-xl mb-6 border-0"
      title={
        <div className="flex justify-between items-start flex-wrap gap-2 w-full">
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <Title 
              level={5} 
              className={` text-blue-600 m-0 flex items-center gap-2 ${
              isMobile ? 'text-base' : 'text-lg'
              }`}
            >
              {t("To Be Approved")}
            </Title>
          </div>
          {/* <Badge 
            count={data.length} 
            showZero 
            className={` ${isMobile ? 'text-xs' : 'text-sm'}`}
          /> */}
        </div>
      }
    >      {loader ? (
        <div className="py-5">
          <Skeleton active paragraph={{ rows: 4 }} />
        </div>
      ) : dataArray.length === 0 ? (
        <Empty
          description={
            <Space direction="vertical" size="small">
              <Text type="secondary">{t("No pending approvals")}</Text>
              <Text type="secondary" className="text-xs">
                {t("All caught up! No requests require your approval at this time.")}
              </Text>
            </Space>
          }
          className="py-10 px-5"
        />      ) : (
        <div className="w-full">
          {/* Paginated Grid Layout - 2x2 on mobile, responsive on larger screens */}
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-3 sm:gap-4 w-full">
            {currentItems.map((item: any, index: number) => (
              <div key={startIndex + index} className="w-full">
                <RequestCard {...item} spaces={spaces} workflow={workflows[item.workflowId]} />
              </div>
            ))}
          </div>
          
          {/* Pagination Controls */}
          {dataArray.length > ITEMS_PER_PAGE && (
            <div className="flex justify-between items-center w-full mt-6 px-2">
              <Button 
                type="default" 
                icon={<LeftOutlined />} 
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                size={isMobile ? "small" : "middle"}
                className="flex items-center gap-1"
              >
                {!isMobile && t("Previous")}
              </Button>
              
              <div className="flex items-center gap-2">
                <span className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  {t('Page')} {currentPage} of {totalPages}
                </span>
                <span className={`text-gray-400 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  {dataArray.length} {t("total")}
                </span>
              </div>
                <Button 
                type="default" 
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                size={isMobile ? "small" : "middle"}
                className="flex items-center gap-1"
              >
                {!isMobile && t("Next")}
                <RightOutlined />
              </Button>
            </div>
          )}
          
          {/* Show single page info when no pagination needed */}
          {dataArray.length <= ITEMS_PER_PAGE && (
            <div className={`flex justify-center items-center w-full ${isMobile ? 'mt-4' : 'mt-6'}`}>
              <div className={`text-gray-500 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                {t('Showing all')} {dataArray.length} items
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default ApprovalsPending;
